<template>
    <div class="flex-1 flex flex-col overflow-hidden min-h-0">
        <div class="shrink-0 bg-surface-raised border-b border-border px-6 py-4">
            <div class="max-w-3xl mx-auto flex items-center justify-between">
                <div>
                    <h1 class="title-body-bold text-ink">IMPACT Leader Code Self-Reflection</h1>
                    <p class="body-small text-ink-muted mt-1">Your results</p>
                </div>
            </div>
        </div>
        <div class="flex-1 overflow-y-auto">
            <div class="max-w-3xl mx-auto px-6 py-10 space-y-6">
                <div class="grid gap-4" :class="coaching_session_url && gather_feedback_url ? 'grid-cols-2' : 'grid-cols-1'">
                    <div v-if="coaching_session_url" class="bg-surface-raised rounded-2xl border border-border shadow-sm p-5 flex flex-col gap-4">
                        <div class="w-9 h-9 rounded-lg bg-chip-nadia-bg flex items-center justify-center shrink-0">
                            <i class="bi bi-stars text-chip-nadia-text text-sm" aria-hidden="true"></i>
                        </div>
                        <div class="flex-1">
                            <p class="body-bold text-ink mb-1">Coach with Nadia</p>
                            <p class="body-small text-ink-muted">Turn your results into a focused development plan with your AI coach.</p>
                        </div>
                        <a :href="coaching_session_url" class="base-btn-primary base-btn-sm shrink-0 text-center whitespace-normal sm:whitespace-nowrap">
                            {{ isNewlySubmitted ? "Start coaching →" : "Continue chat →" }}
                        </a>
                    </div>

                    <div v-if="gather_feedback_url" class="bg-surface-raised rounded-2xl border border-border shadow-sm p-5 flex flex-col gap-4">
                        <div class="w-9 h-9 rounded-lg bg-surface-sunken flex items-center justify-center shrink-0">
                            <i class="bi bi-people text-ink text-sm" aria-hidden="true"></i>
                        </div>
                        <div class="flex-1">
                            <p class="body-bold text-ink mb-1">Gather peer feedback</p>
                            <p class="body-small text-ink-muted">See how others experience your leadership through anonymous peer feedback.</p>
                        </div>
                        <a :href="gather_feedback_url" class="base-btn-neutral base-btn-sm w-full text-center whitespace-normal sm:whitespace-nowrap"> Gather feedback → </a>
                    </div>
                </div>

                <div class="bg-surface-raised rounded-2xl border border-border shadow-sm overflow-hidden">
                    <div class="px-6 py-4 border-b border-border flex items-center gap-3">
                        <div class="w-8 h-8 rounded-lg bg-chip-status-done-bg flex items-center justify-center shrink-0">
                            <i class="bi bi-arrow-up-circle text-chip-status-done-text text-sm" aria-hidden="true"></i>
                        </div>
                        <h3 class="body-bold text-ink">Your Greatest Strengths</h3>
                        <span class="details text-ink-muted ml-auto">Rated 4–5</span>
                    </div>
                    <div v-if="completedStrengths.length" class="divide-y divide-border">
                        <div v-for="q in completedStrengths" :key="q.index" class="px-6 py-3 flex items-center justify-between gap-4">
                            <div>
                                <span class="body-small-semibold text-ink">{{ q.title }}</span>
                                <span class="details text-ink-muted ml-2">{{ q.competency }}</span>
                            </div>
                            <div class="shrink-0 details px-2.5 py-1 rounded-full bg-chip-status-done-bg text-chip-status-done-text">
                                {{ completed_responses?.[q.index] ?? 0 }} · {{ rating_labels[String(completed_responses?.[q.index] ?? 0)] }}
                            </div>
                        </div>
                    </div>
                    <div v-else class="px-6 py-4 body-small text-ink-muted">No behaviors rated 4 or above.</div>
                </div>

                <div class="bg-surface-raised rounded-2xl border border-border shadow-sm overflow-hidden">
                    <div class="px-6 py-4 border-b border-border flex items-center gap-3">
                        <div class="w-8 h-8 rounded-lg bg-chip-status-pending-bg flex items-center justify-center shrink-0">
                            <i class="bi bi-arrow-up-right-circle text-chip-status-pending-text text-sm" aria-hidden="true"></i>
                        </div>
                        <h3 class="body-bold text-ink">Areas of Opportunity</h3>
                        <span class="details text-ink-muted ml-auto">Rated 1–2</span>
                    </div>
                    <div v-if="completedOpportunities.length" class="divide-y divide-border">
                        <div v-for="q in completedOpportunities" :key="q.index" class="px-6 py-3 flex items-center justify-between gap-4">
                            <div>
                                <span class="body-small-semibold text-ink">{{ q.title }}</span>
                                <span class="details text-ink-muted ml-2">{{ q.competency }}</span>
                            </div>
                            <div class="shrink-0 details px-2.5 py-1 rounded-full bg-chip-status-pending-bg text-chip-status-pending-text">
                                {{ completed_responses?.[q.index] ?? 0 }} · {{ rating_labels[String(completed_responses?.[q.index] ?? 0)] }}
                            </div>
                        </div>
                    </div>
                    <div v-else class="px-6 py-4 body-small text-ink-muted">No behaviors rated 2 or below.</div>
                </div>

                <div class="bg-surface-raised rounded-2xl border border-border shadow-sm overflow-hidden">
                    <div class="px-6 py-4 border-b border-border">
                        <h3 class="body-bold text-ink">Full Breakdown</h3>
                    </div>
                    <div v-for="(group, i) in groupedQuestions" :key="i" class="border-b border-border last:border-0">
                        <div class="px-6 py-2.5 bg-surface-sunken">
                            <!-- One-off: 12px font-bold tracking-widest eyebrow; no exact utility match -->
                            <span class="details font-bold tracking-widest text-ink uppercase">{{ group.competency }}</span>
                        </div>
                        <div v-for="q in group.questions" :key="q.index" class="px-6 py-2.5 flex items-center justify-between gap-4 border-t border-border first:border-0">
                            <span class="body-small text-ink-muted">{{ q.title }}</span>
                            <div
                                :class="[
                                    'shrink-0 details px-2.5 py-1 rounded-full',
                                    (completed_responses?.[q.index] ?? 0) === 0
                                        ? 'bg-surface-sunken text-ink-muted'
                                        : (completed_responses?.[q.index] ?? 0) >= 4
                                          ? 'bg-chip-status-done-bg text-chip-status-done-text'
                                          : 'bg-chip-status-pending-bg text-chip-status-pending-text',
                                ]">
                                {{
                                    (completed_responses?.[q.index] ?? 0) === 0
                                        ? "—"
                                        : `${completed_responses?.[q.index] ?? 0} · ${rating_labels[String(completed_responses?.[q.index] ?? 0)]}`
                                }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="text-center pb-4">
                    <template v-if="!confirmingRetake">
                        <BaseButton theme="tertiary" size="sm" @click="emit('start-retake')">Retake self-reflection</BaseButton>
                    </template>
                    <template v-else>
                        <p class="body-small text-ink-muted mb-2">This will clear your previous results. Are you sure?</p>
                        <p v-if="retakeError" role="alert" class="details text-notification-error-text mb-2">{{ retakeError }}</p>
                        <div class="flex justify-center gap-3">
                            <BaseButton theme="danger" size="sm" :disabled="isRetaking" @click="emit('confirm-retake')">
                                <span v-if="isRetaking">Resetting...</span>
                                <span v-else>Yes, retake</span>
                            </BaseButton>
                            <BaseButton theme="tertiary" size="sm" :disabled="isRetaking" @click="emit('cancel-retake')"> Cancel </BaseButton>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";

interface Question {
    index: number;
    competency: string;
    title: string;
    description: string;
}

interface QuestionGroup {
    competency: string;
    definition: string;
    questions: Question[];
}

interface Props {
    coaching_session_url?: string | null;
    gather_feedback_url?: string | null;
    completedStrengths: Question[];
    completedOpportunities: Question[];
    groupedQuestions: QuestionGroup[];
    completed_responses?: Record<number, number>;
    rating_labels: Record<string, string>;
    confirmingRetake: boolean;
    isRetaking: boolean;
    retakeError: string | null;
    isNewlySubmitted: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
    (e: "start-retake"): void;
    (e: "cancel-retake"): void;
    (e: "confirm-retake"): void;
}>();
</script>
